import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import SEO from '../../components/SEO';
import LandingNavBar from '../../components/LandingNavBar';
import LandingFooter from '../../components/LandingFooter';
import { HRPromoCard } from '../../components/HRPromoBanner';
import {
  Check, ArrowRight, Clock,
  Zap, Building2, Crown, Bed, FileText, Plug,
  Gift, Settings, Headphones, Sparkles, Shield, Lock, Ticket
} from 'lucide-react';

const heading = { fontFamily: "'Plus Jakarta Sans', 'Manrope', sans-serif" };
const body = { fontFamily: "'Manrope', sans-serif" };

const Pricing = () => {
  useEffect(() => { window.scrollTo(0, 0); }, []);

  const [etLoading, setEtLoading] = useState('');
  const startEticketSubscription = async (planId = 'full_suite') => {
    if (etLoading) return;
    setEtLoading(planId);
    try {
      const API = process.env.REACT_APP_BACKEND_URL;
      const res = await axios.post(`${API}/api/eticket/subscription/checkout`, { origin_url: window.location.origin, plan_id: planId });
      if (res.data?.url) window.location.href = res.data.url;
      else setEtLoading('');
    } catch (e) {
      setEtLoading('');
      alert('Could not start checkout. Please try again in a moment.');
    }
  };

  const eticketPlans = [
    { id: 'eticket', name: 'E-Ticket', price: 'RM299', fee: '0% fee', sub: 'Ticketing', featured: false },
    { id: 'eticket_einvoice', name: 'E-Ticket + E-Invoice', price: 'RM399', fee: '0% fee', sub: 'Ticketing + E-Invoice', featured: false },
    { id: 'eticket_sgmc', name: 'E-Ticket + SGMC', price: 'RM399', fee: '+ 3% SGMC', sub: 'Ticketing + Global Distribution', featured: false },
    { id: 'full_suite', name: 'Full Suite', price: 'RM499', fee: '+ 3% SGMC', sub: 'Everything, together', featured: true },
  ];

  const plans = [
    {
      name: 'Starter',
      desc: 'For small homestays getting started',
      originalPrice: 'RM 99',
      price: 'RM 59',
      per: '/mo',
      rooms: 'Up to 10 rooms',
      icon: Bed,
      iconGradient: 'from-slate-500 to-slate-700',
      features: [
        'Seamless PMS integration',
        'Auto e-Invoice generation',
        'Manual submission mode',
        'PDF + QR code output',
        'Email support (72hr)'
      ],
      cta: 'Start Free Trial',
      ctaLink: '/einvoice/portal/auth'
    },
    {
      name: 'Growth',
      desc: 'Best for Growing Hotels',
      originalPrice: 'RM 199',
      price: 'RM 119',
      per: '/mo',
      rooms: 'Up to 30 rooms',
      icon: Zap,
      iconGradient: 'from-blue-500 to-blue-700',
      recommended: true,
      features: [
        'Everything in Starter, plus:',
        'Auto-submit to LHDN',
        'Individual + Consolidated invoices',
        'Bulk submission (50+ at once)',
        'Smart pre-validation',
        'Customer TIN management',
        'Email + WhatsApp support (24hr)'
      ],
      cta: 'Start Free Trial',
      ctaLink: '/einvoice/portal/auth'
    },
    {
      name: 'Pro',
      desc: 'Multi-property control & insights',
      originalPrice: 'RM 299',
      price: 'RM 199',
      per: '/mo',
      rooms: 'Up to 50 rooms',
      icon: Building2,
      iconGradient: 'from-purple-500 to-purple-700',
      features: [
        'Everything in Growth, plus:',
        'Multi-property dashboard',
        'Role-based access',
        'Advanced analytics & reports',
        'Audit trail logs',
        'Export reports (Excel/CSV)',
        'Priority processing',
        'Phone support'
      ],
      cta: 'Start Free Trial',
      ctaLink: '/einvoice/portal/auth'
    },
    {
      name: 'Enterprise',
      desc: 'For hotel chains & partners',
      price: 'Custom',
      per: '',
      rooms: 'Unlimited rooms',
      icon: Crown,
      iconGradient: 'from-amber-400 to-amber-600',
      isEnterprise: true,
      features: [
        'Unlimited properties',
        'Unlimited PMS accounts',
        'Everything in Pro, plus:',
        'API access',
        'Custom integrations',
        'White-label option',
        'Dedicated account manager',
        'SLA guarantee (99.9%)',
        'Custom training'
      ],
      cta: 'Contact Sales',
      ctaLink: '/contact'
    }
  ];

  const bonuses = [
    {
      icon: Gift,
      gradient: 'from-rose-400 to-orange-400',
      shadow: 'shadow-rose-500/20',
      title: 'Done-For-You Onboarding',
      desc: 'We set up everything — no technical work needed',
      value: 'RM299',
      highlight: true
    },
    {
      icon: Settings,
      gradient: 'from-indigo-400 to-purple-500',
      shadow: 'shadow-indigo-500/20',
      title: 'PMS / Channel Manager Integration',
      desc: 'We connect your system and ensure seamless syncing',
      value: 'RM200'
    },
    {
      icon: FileText,
      gradient: 'from-emerald-400 to-teal-500',
      shadow: 'shadow-emerald-500/20',
      title: 'E-Invoice Playbook (Malaysia Edition)',
      desc: 'Step-by-step guide to LHDN compliance',
      value: 'RM150'
    },
    {
      icon: Headphones,
      gradient: 'from-amber-400 to-orange-500',
      shadow: 'shadow-amber-500/20',
      title: 'Priority Support (First 30 Days)',
      desc: 'Faster responses & direct help during launch',
      value: 'RM350'
    }
  ];

  return (
    <div className="min-h-screen bg-white" style={body} data-testid="pricing-page">
      <SEO
        title="Pricing — e-Invoice Malaysia Plans for Hotels & PMS | From RM59/mo"
        description="StayMii e-invoice pricing from RM59/month. Automated LHDN e-invoicing plans for Malaysian hotels, Airbnb hosts & short-term rentals. Integrates with your PMS. No system change required."
        keywords="e invoice malaysia pricing, hotel e invoice pricing, lhdn e invoice pricing, hotel management system malaysia pricing, airbnb invoicing malaysia, myinvois pricing plans"
        canonicalUrl="/pricing"
      />

      <LandingNavBar />

      {/* ═══ HERO ═══ */}
      <section className="relative pt-32 pb-20 bg-gradient-to-br from-[#0F172A] via-[#1E293B] to-[#0F172A] overflow-hidden" data-testid="pricing-hero">
        {/* Ambient glow */}
        <div className="absolute top-[-120px] left-1/4 w-96 h-96 bg-blue-500/10 rounded-full blur-3xl pointer-events-none" />
        <div className="absolute bottom-0 right-1/4 w-96 h-96 bg-indigo-500/10 rounded-full blur-3xl pointer-events-none" />

        <div className="relative max-w-4xl mx-auto px-6 text-center">
          {/* Micro badge */}
          <div className="inline-flex items-center gap-2 px-4 py-1.5 mb-8 bg-amber-500/10 border border-amber-500/20 rounded-full" data-testid="founding-badge">
            <Sparkles className="w-3.5 h-3.5 text-amber-400" />
            <span className="text-xs font-bold text-amber-400 uppercase tracking-wider">Founding Member Pricing</span>
          </div>

          <h1
            className="text-5xl md:text-6xl font-extrabold tracking-tight mb-5 text-white"
            style={heading}
          >
            e-Invoice Malaysia Pricing<br className="hidden sm:block" />
            <span className="text-[#60A5FA]">for Hotels & PMS</span>
          </h1>
          <p className="text-lg md:text-xl text-white/80 max-w-2xl mx-auto mb-6 leading-relaxed">
            Automate LHDN e-invoicing from your existing PMS. Plans from RM59/month.
            Lock your founding member price forever.
          </p>
          <p className="text-white/40 text-sm flex items-center justify-center gap-2">
            <Plug className="h-3.5 w-3.5" />
            StayMii integrates with your PMS. No system change required.
          </p>

          {/* Trust indicators */}
          <div className="flex flex-wrap justify-center gap-5 mt-8">
            {['Proprietary Software', 'Designed for Compliance', 'Built for Hospitality'].map((item, i) => (
              <div key={i} className="flex items-center gap-2">
                <Check className="w-3.5 h-3.5 text-emerald-400" />
                <span className="text-white/50 text-xs font-medium">{item}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ═══ SOCIAL PROOF BAR ═══ */}
      <div className="bg-[#2563EB] py-4">
        <div className="max-w-5xl mx-auto px-6">
          <div className="flex flex-wrap justify-center gap-6">
            <div className="flex items-center gap-2.5">
              <div className="w-2 h-2 rounded-full bg-white animate-pulse" />
              <span className="text-sm text-white font-medium">Costs less than 1 room booking/month</span>
            </div>
            <div className="flex items-center gap-2.5">
              <div className="w-2 h-2 rounded-full bg-white animate-pulse" />
              <span className="text-sm text-white font-medium">Save 10+ hours/month on manual e-Invoice work</span>
            </div>
          </div>
        </div>
      </div>

      {/* ═══ PRICING CARDS ═══ */}
      <section id="einvoice-pricing" className="pt-16 pb-24 px-6" data-testid="einvoice-pricing-section">
        <div className="max-w-7xl mx-auto">
          {/* Trust strip */}
          <div className="flex flex-wrap justify-center gap-6 mb-12" data-testid="pricing-trust-strip">
            {[
              { icon: Shield, label: 'Protected Software' },
              { icon: Check, label: 'Built for Malaysia Compliance' },
              { icon: Lock, label: 'Proprietary Technology' },
            ].map((item, i) => (
              <div key={i} className="flex items-center gap-2.5 px-5 py-2.5 bg-slate-50 rounded-xl border border-slate-100">
                <item.icon className="w-4 h-4 text-[#2563EB]" />
                <span className="text-sm text-slate-600 font-medium">{item.label}</span>
              </div>
            ))}
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 items-stretch">
            {plans.map((plan) => {
              const isRec = plan.recommended;
              const isEnt = plan.isEnterprise;

              return (
                <div
                  key={plan.name}
                  className={`relative rounded-2xl p-7 flex flex-col transition-all duration-300 group
                    ${isRec
                      ? 'bg-[#0F172A] border-2 border-blue-500/60 shadow-[0_0_50px_rgba(59,130,246,0.12)] lg:-translate-y-3 z-10'
                      : isEnt
                      ? 'bg-gradient-to-b from-[#1a1505] to-[#0B1120] border border-amber-500/20'
                      : 'bg-[#0B1120] border border-[#1E293B] hover:border-[#334155] hover:-translate-y-1'
                    }`}
                  data-testid={`einvoice-plan-${plan.name.toLowerCase()}`}
                >
                  {/* Recommended badge */}
                  {isRec && (
                    <div className="absolute -top-3.5 left-1/2 -translate-x-1/2">
                      <span className="px-4 py-1 bg-blue-500 text-white text-[10px] font-bold rounded-full uppercase tracking-wider whitespace-nowrap">
                        Most Popular
                      </span>
                    </div>
                  )}

                  {/* Founding price badge */}
                  {!isEnt && (
                    <div className="mb-5">
                      <span className="inline-flex items-center gap-1.5 px-3 py-1 bg-amber-500/10 border border-amber-500/15 rounded-full text-[10px] font-bold text-amber-400 uppercase tracking-wider">
                        <Clock className="w-3 h-3" /> Founding Price
                      </span>
                    </div>
                  )}

                  {/* Icon */}
                  <div className={`w-11 h-11 rounded-xl bg-gradient-to-br ${plan.iconGradient} flex items-center justify-center mb-4 shadow-lg ${isEnt ? 'shadow-amber-500/20' : ''}`}>
                    <plan.icon className="w-5 h-5 text-white" />
                  </div>

                  {/* Name & desc */}
                  <h3 className="text-xl font-bold text-white mb-1" style={heading}>{plan.name}</h3>
                  <p className="text-sm text-slate-500 mb-5">{plan.desc}</p>

                  {/* Price */}
                  <div className="mb-5">
                    {plan.originalPrice && (
                      <span className="text-base text-slate-600 line-through mr-2">{plan.originalPrice}</span>
                    )}
                    <span className="text-4xl font-extrabold text-white" style={heading}>{plan.price}</span>
                    {plan.per && <span className="text-sm text-slate-500 ml-1">{plan.per}</span>}
                  </div>

                  {/* Room badge */}
                  <div className={`inline-flex items-center gap-2 px-3 py-2 rounded-lg mb-6 ${
                    isEnt ? 'bg-amber-500/10 border border-amber-500/15' : 'bg-slate-800/50 border border-slate-700/50'
                  }`}>
                    <Bed className={`h-4 w-4 ${isEnt ? 'text-amber-400' : 'text-slate-400'}`} />
                    <span className={`text-sm font-medium ${isEnt ? 'text-amber-400' : 'text-slate-300'}`}>{plan.rooms}</span>
                  </div>

                  {/* Features */}
                  <ul className="space-y-3 mb-8 flex-1">
                    {plan.features.map((f, i) => (
                      <li key={i} className="flex items-start gap-2.5">
                        <div className={`w-4 h-4 rounded-full flex items-center justify-center mt-0.5 shrink-0 ${
                          isEnt ? 'bg-amber-500/15' : 'bg-emerald-500/15'
                        }`}>
                          <Check className={`w-2.5 h-2.5 ${isEnt ? 'text-amber-400' : 'text-emerald-400'}`} />
                        </div>
                        <span className="text-sm text-slate-400">{f}</span>
                      </li>
                    ))}
                  </ul>

                  {/* CTA */}
                  <Link
                    to={plan.ctaLink}
                    className={`block w-full py-3.5 rounded-xl font-bold text-center text-sm transition-all duration-200
                      ${isRec
                        ? 'bg-blue-500 hover:bg-blue-600 text-white shadow-lg shadow-blue-500/25 hover:shadow-blue-500/40'
                        : isEnt
                        ? 'bg-amber-500 hover:bg-amber-600 text-white shadow-lg shadow-amber-500/20'
                        : 'bg-transparent border border-slate-600 hover:bg-slate-800 text-white'
                      }`}
                    data-testid={`einvoice-cta-${plan.name.toLowerCase()}`}
                  >
                    {plan.cta}
                  </Link>

                  {isRec && (
                    <p className="text-[11px] text-blue-400/60 text-center mt-3 font-medium">
                      Recommended for 80% of properties
                    </p>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* ═══ LAUNCH BONUSES ═══ */}
      <section className="px-6 pb-24" data-testid="launch-bonuses-section">
        <div className="max-w-4xl mx-auto relative">
          {/* Container */}
          <div className="relative rounded-3xl bg-gradient-to-b from-[#0B1120] to-[#020617] border border-[#1E293B] p-8 md:p-12 overflow-hidden">
            {/* Ambient glow inside */}
            <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[500px] h-[200px] bg-blue-600/5 rounded-full blur-[80px] pointer-events-none" />

            {/* Badge */}
            <div className="relative text-center mb-6">
              <span className="inline-flex items-center gap-2 px-4 py-1.5 bg-amber-500/10 border border-amber-500/15 rounded-full text-amber-400 text-xs font-bold uppercase tracking-wider">
                <Clock className="w-3.5 h-3.5" /> Limited Time Launch Offer
              </span>
            </div>

            {/* Headline */}
            <h2 className="relative text-3xl md:text-4xl font-extrabold text-center text-white mb-2 tracking-tight" style={heading}>
              Get <span className="text-emerald-400">RM1,000+</span> in Setup &<br className="hidden sm:block" />
              Support — <span className="text-emerald-400">Free</span>
            </h2>
            <p className="relative text-slate-500 text-sm text-center mb-10">
              Everything you need to go live with e-Invoicing — <strong className="text-slate-300">handled for you.</strong>
            </p>

            {/* Value comparison */}
            <div className="relative flex items-center justify-center gap-6 mb-10">
              <div className="flex items-center gap-3 px-5 py-3 bg-[#0F172A] rounded-xl border border-[#1E293B]">
                <span className="text-[11px] text-slate-500 font-semibold uppercase tracking-wider">Total Value</span>
                <span className="text-lg font-bold text-slate-400 line-through decoration-red-400 decoration-2">RM1,000+</span>
              </div>
              <div className="w-px h-10 bg-[#1E293B]" />
              <div className="text-center">
                <p className="text-[11px] text-slate-500 font-semibold uppercase tracking-wider">You Pay</p>
                <p className="text-3xl font-black text-blue-400" style={heading}>RM0</p>
              </div>
            </div>

            {/* Bonus items */}
            <div className="relative max-w-2xl mx-auto space-y-3 mb-10">
              {bonuses.map((b, i) => (
                <div
                  key={i}
                  className={`rounded-xl p-5 flex items-center gap-4 transition-colors duration-200 ${
                    b.highlight
                      ? 'bg-[#0F172A] border-2 border-blue-500/30 hover:border-blue-500/50'
                      : 'bg-[#0F172A] border border-[#1E293B] hover:bg-[#131C2D]'
                  }`}
                >
                  {/* Highlight badge */}
                  {b.highlight && (
                    <div className="absolute -mt-16 ml-auto mr-4 self-start">
                      <span className="px-3 py-0.5 bg-blue-500 text-white text-[9px] font-bold rounded-full uppercase tracking-wider">
                        Most Valuable
                      </span>
                    </div>
                  )}
                  <div className={`w-12 h-12 rounded-xl bg-gradient-to-br ${b.gradient} flex items-center justify-center shrink-0 shadow-lg ${b.shadow}`}>
                    <b.icon className="w-5 h-5 text-white" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-bold text-white">{b.title}</p>
                    <p className="text-xs text-slate-500">{b.desc}</p>
                    {b.value && (
                      <span className="inline-block mt-1 text-[10px] font-bold text-emerald-400 uppercase tracking-wider">
                        Valued at {b.value}
                      </span>
                    )}
                  </div>
                  <div className="w-7 h-7 bg-emerald-500/15 rounded-full flex items-center justify-center shrink-0">
                    <Check className="w-3.5 h-3.5 text-emerald-400" />
                  </div>
                </div>
              ))}
            </div>

            {/* Urgency */}
            <div className="relative text-center mb-8">
              <span className="inline-flex items-center gap-2 text-slate-600 text-xs font-medium">
                <Clock className="w-3.5 h-3.5" /> Limited-time launch offer — may be removed anytime
              </span>
            </div>

            {/* CTA */}
            <div className="relative text-center">
              <Link
                to="/einvoice/portal/auth"
                className="group inline-flex items-center justify-center gap-3 px-10 py-4 bg-blue-500 hover:bg-blue-600 text-white rounded-2xl font-bold text-lg transition-all shadow-lg shadow-blue-500/25 hover:shadow-blue-500/40 w-full sm:w-auto"
                data-testid="launch-bonus-cta"
              >
                Start Free Setup Now
                <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
              </Link>
              <p className="text-slate-600 text-xs mt-4">Takes less than 2 minutes &middot; No commitment</p>
            </div>

            {/* Trust badges */}
            <div className="relative flex flex-col sm:flex-row items-center justify-center gap-6 mt-8 pt-8 border-t border-[#1E293B]">
              <div className="flex items-center gap-2.5">
                <div className="w-6 h-6 bg-emerald-500/15 rounded-full flex items-center justify-center">
                  <Check className="w-3.5 h-3.5 text-emerald-400" />
                </div>
                <div>
                  <p className="text-xs font-bold text-slate-300">Used & Recommended</p>
                  <p className="text-[10px] text-slate-600">Malaysian hotels & homestays</p>
                </div>
              </div>
              <div className="flex items-center gap-2.5">
                <div className="w-6 h-6 bg-emerald-500/15 rounded-full flex items-center justify-center">
                  <Check className="w-3.5 h-3.5 text-emerald-400" />
                </div>
                <div>
                  <p className="text-xs font-bold text-slate-300">LHDN-Ready</p>
                  <p className="text-[10px] text-slate-600">100% e-Invoice compliant</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ═══ BOTTOM CTA ═══ */}
      <section className="py-24 bg-gradient-to-br from-[#2563EB] via-[#1D4ED8] to-[#1E40AF] relative overflow-hidden" data-testid="pricing-cta">
        <div className="max-w-3xl mx-auto px-6 text-center">
          <h2 className="text-3xl md:text-5xl font-extrabold text-white mb-5 tracking-tight" style={heading}>
            Ready to Automate<br />Your Compliance?
          </h2>
          <p className="text-lg text-white/90 mb-10 max-w-xl mx-auto leading-relaxed">
            Join hotels across Malaysia using StayMii to automate e-invoicing and stay LHDN-compliant.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              to="/einvoice/portal/auth"
              className="group px-8 py-4 bg-white text-[#2563EB] rounded-xl font-bold text-lg hover:shadow-2xl hover:shadow-white/30 transition-all transform hover:-translate-y-1 flex items-center justify-center gap-2"
              data-testid="pricing-start-btn"
            >
              Start Free Trial
              <ArrowRight className="h-5 w-5 group-hover:translate-x-1 transition-transform" />
            </Link>
            <Link
              to="/contact"
              className="px-8 py-4 bg-white/10 backdrop-blur-sm text-white rounded-xl font-bold text-lg hover:bg-white/20 transition-all border border-white/30 flex items-center justify-center gap-2"
              data-testid="pricing-contact-btn"
            >
              Contact Sales
            </Link>
          </div>
        </div>
      </section>

      {/* ═══ STAYMII E-TICKET PLANS ═══ */}
      <section className="py-16 bg-slate-50 border-t border-slate-100" data-testid="eticket-pricing-band">
        <div className="max-w-6xl mx-auto px-6">
          <div className="text-center max-w-2xl mx-auto mb-10">
            <div className="inline-flex items-center gap-2 mb-3">
              <Ticket className="w-5 h-5 text-cyan-600" />
              <span className="text-xs font-bold text-slate-700 uppercase tracking-widest">StayMii E-Ticket</span>
            </div>
            <h2 className="text-2xl sm:text-3xl font-extrabold text-slate-900 mb-2" style={heading}>Ticketing plans for attractions</h2>
            <p className="text-slate-500 text-sm">RM299 ticketing · RM399 + e-Invoice · RM399 + global distribution (SGMC) · RM499 full suite.</p>
          </div>
          <div className="grid sm:grid-cols-2 xl:grid-cols-4 gap-5 items-stretch">
            {eticketPlans.map((p) => (
              <div key={p.id}
                className={`relative flex flex-col rounded-2xl border p-6 transition-all ${p.featured ? 'border-cyan-400 bg-gradient-to-b from-cyan-50 to-white shadow-xl ring-2 ring-cyan-200' : 'border-slate-200 bg-white shadow-sm hover:shadow-lg hover:-translate-y-1'}`}
                data-testid={`eticket-plan-${p.id}`}>
                {p.featured && <div className="absolute -top-3 left-1/2 -translate-x-1/2 bg-cyan-500 text-white text-[10px] font-bold tracking-widest px-3 py-1 rounded-full shadow">RECOMMENDED</div>}
                <span className="text-[11px] font-bold text-slate-400 uppercase tracking-wide mb-1">{p.sub}</span>
                <h3 className="text-base font-extrabold text-slate-900 mb-3" style={heading}>{p.name}</h3>
                <div className="flex items-end gap-1 mb-1">
                  <span className="text-3xl font-black text-slate-900 tracking-tighter" style={heading}>{p.price}</span>
                  <span className="text-slate-400 font-semibold text-xs mb-1">/mo</span>
                </div>
                <span className={`inline-flex self-start text-[11px] font-bold px-2 py-0.5 rounded-full mb-5 ${p.fee.includes('0%') ? 'bg-emerald-50 text-emerald-700' : 'bg-slate-100 text-slate-600'}`}>{p.fee}</span>
                <button onClick={() => startEticketSubscription(p.id)} disabled={!!etLoading} data-testid={`eticket-subscribe-${p.id}`}
                  className={`mt-auto w-full inline-flex items-center justify-center gap-2 px-4 py-3 rounded-xl font-black text-sm transition-colors disabled:opacity-70 ${p.featured ? 'bg-cyan-500 hover:bg-cyan-600 text-white' : 'bg-slate-900 hover:bg-slate-800 text-white'}`}>
                  {etLoading === p.id ? 'Redirecting…' : <>Subscribe <ArrowRight className="w-4 h-4" /></>}
                </button>
              </div>
            ))}
          </div>
          <div className="text-center mt-8">
            <Link to="/e-ticket" data-testid="eticket-learn-more"
              className="inline-flex items-center justify-center gap-2 px-6 py-3 bg-white hover:bg-slate-50 text-slate-800 border border-slate-200 rounded-xl font-semibold text-sm transition-colors">
              Compare plans & features <ArrowRight className="w-4 h-4" />
            </Link>
            <p className="text-xs text-slate-400 mt-4">SGMC = StayMii Global Market Connect · 3% applies only to SGMC-channel bookings (Bókun, GlobalTix & more), never to walk-ins or direct sales.</p>
          </div>
        </div>
      </section>

      {/* ═══ RELATED e-INVOICE PAGES ═══ */}
      <section className="py-16 bg-white" data-testid="related-pages-section">
        <div className="max-w-5xl mx-auto px-6">
          <h2 className="text-2xl font-bold text-slate-900 mb-8 text-center" style={heading}>Explore e-Invoice Malaysia</h2>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {[
              { to: '/e-invoice-malaysia', label: 'e-Invoice Malaysia Guide' },
              { to: '/lhdn-e-invoice', label: 'LHDN e-Invoice Compliance' },
              { to: '/hotel-e-invoice-system', label: 'Hotel e-Invoice System' },
              { to: '/pms-e-invoice-integration', label: 'PMS Integration' },
              { to: '/channel-manager-e-invoice', label: 'Channel Manager e-Invoice' },
              { to: '/airbnb-e-invoice-malaysia', label: 'Airbnb e-Invoice' },
              { to: '/features', label: 'All Features' },
              { to: '/integrations', label: 'Integrations' },
            ].map((link, i) => (
              <Link key={i} to={link.to} className="block p-3 bg-slate-50 hover:bg-blue-50 rounded-xl border border-slate-100 hover:border-blue-200 transition-all text-center">
                <p className="font-medium text-slate-700 hover:text-[#2563EB] text-sm">{link.label}</p>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* ═══ SUBTLE LITE REDIRECT ═══ */}
      <div className="py-6 bg-[#F97316] text-center" data-testid="lite-redirect">
        <p className="text-white text-sm">
          No system yet?{' '}
          <Link to="/get-started" className="text-white font-semibold underline underline-offset-2 hover:text-white/80 transition-colors">
            Start simple with StayMii Lite
          </Link>.
        </p>
      </div>

      <HRPromoCard />

      <LandingFooter />
    </div>
  );
};

export default Pricing;
